import { fieldForDataPoint } from '@dealstack/shared/lib/common/business/documentTemplate';
import { fieldIdentity } from '@dealstack/shared/lib/documents/scopedFieldNames';

import { uniqueFieldName } from './fieldNameFromPath';
import type { FieldSuggestionItem } from './extensions/fieldSuggestion';

// Re-exported so the palette's callers keep one import site, while the derivation itself stays
// shared with the .docx import that binds by the same name.
export { fieldForDataPoint };

export interface CataloguePoint {
	group: string;
	label: string;
	/** A Liquid expression against the render root, filters and all. */
	path: string;
	sampleValue?: string;
	/** Set when the name must NOT be derived from the label — see `DataPointName.fieldName`. */
	fieldName?: string;
}

export interface OfferedFields {
	items: FieldSuggestionItem[];
	/** Field name → the Liquid expression to store for it. Sent with the save. */
	bindings: Record<string, string>;
	/**
	 * Field name → label, for the catalogue points not offered. Named and bound exactly as if they
	 * were, so a field the document already holds keeps its name, its link and its label.
	 */
	withheld: Record<string, string>;
}

/**
 * The catalogue as fields the editor can offer, with the binding to store for each.
 *
 * `existing` is what the document already holds (name → binding, undefined when unlinked). A
 * name it already uses is REUSED when it is unlinked or already points at this same data point:
 * minting `investor.first_name_2` beside an `investor.first_name` the .docx brought in bound the
 * same value twice under two names, offered the author two identical-looking entries, and put
 * the suffix on the chip. A suffix is only correct when the name is taken by something else.
 */
export const offeredFields = (
	catalogue: readonly CataloguePoint[],
	existing: ReadonlyMap<string, string | undefined>,
	isOffered: (point: CataloguePoint) => boolean = () => true
): OfferedFields => {
	const taken = new Set<string>(existing.keys());
	const items: FieldSuggestionItem[] = [];
	const bindings: Record<string, string> = {};
	const withheld: Record<string, string> = {};
	// The document's own names by identity (see `fieldIdentity`): a per-scheme point is slugged
	// from the scheme's mutable name, so a rename would otherwise offer a duplicate entry.
	const documentNameByIdentity = new Map<string, string>();
	for (const name of existing.keys()) {
		const identity = fieldIdentity(name);
		if (identity !== null && !documentNameByIdentity.has(identity)) {
			documentNameByIdentity.set(identity, name);
		}
	}

	const entries = catalogue.map((point) => ({ point, derived: fieldForDataPoint(point) }));

	// Adopting a placed name on an identity two entries share gave the second one the first's
	// field, writing its expression there. Identity is the whole id now, so this cannot arise
	// from distinct securities — kept as an invariant: only a sole claimant may adopt a name.
	const identityCounts = new Map<string, number>();
	for (const { derived } of entries) {
		const identity = fieldIdentity(derived.name);
		if (identity !== null) {
			identityCounts.set(identity, (identityCounts.get(identity) ?? 0) + 1);
		}
	}

	for (const { point, derived } of entries) {
		const identity = fieldIdentity(derived.name);
		const placedAs =
			identity === null || (identityCounts.get(identity) ?? 0) > 1
				? undefined
				: documentNameByIdentity.get(identity);
		const inDocument = existing.get(placedAs ?? derived.name);
		const reusable =
			!taken.has(derived.name) ||
			(existing.has(derived.name) &&
				(inDocument === undefined || inDocument === derived.binding));
		const name = placedAs ?? (reusable ? derived.name : uniqueFieldName(derived.name, taken));
		taken.add(name);
		bindings[name] = derived.binding;
		if (!isOffered(point)) {
			withheld[name] = point.label;
			continue;
		}
		items.push({
			name,
			label: point.label,
			group: point.group,
			...(derived.filter === undefined ? {} : { filter: derived.filter }),
			...(point.sampleValue === undefined || point.sampleValue === ''
				? {}
				: { sample: point.sampleValue }),
			// A path pinned to the first item of a list drops every other item, which the label
			// alone ("Security name") gives the author no way to know.
			...(/\[0\]/.test(point.path) ? { firstOfList: true } : {}),
		});
	}

	return { items, bindings, withheld };
};
